/**
 * The one container: anything that is one thing (a record, a document, a run) opens here, from the right, with the
 * stage still visible and live. At ≥ 1000 px the panel pushes the stage (PeekLayout makes the flex row); below that
 * it overlays the stage behind a scrim, and under 720 px it is the whole screen. Peeks stack two deep with a small
 * breadcrumb back to the first; Esc closes the top one, the scrim closes all, "Open full" promotes a document to
 * its own page and "Copy link" copies the link of anything else. No dialog, no drawer besides this one.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useEnv } from '../lib/envs.tsx';
import { BackIcon, CloseIcon } from '../components/icons.tsx';
import { IconButton, Kicker, NounTag, cx, noun } from '../design/primitives.tsx';
import { encodePeek, usePeek, type MessageRef, type PeekRef } from './usePeek.ts';
import { RecordPeek } from './RecordPeek.tsx';
import { DocumentPeek } from './DocumentPeek.tsx';
import { RunPeek } from './RunPeek.tsx';
import { IntegrationPeek, SystemPeek } from './ThingPeek.tsx';
import { NetworkIcon } from '../components/icons.tsx';
import { integrationPath, systemPath } from '../lib/systemsview.ts';
import { stripSystem, systemName } from '../lib/systems.ts';
import './peek.css';

export { usePeek, encodePeek, parsePeek, parseStack, PEEK_PARAM, type MessageRef, type PeekRef } from './usePeek.ts';

/** A document's own page: what "Open full" and shared links use. */
export const documentPath = (r: MessageRef) => `/d/${r.vaultId}/${encodeURIComponent(r.uuid)}${r.day ? `?day=${r.day}` : ''}`;

/**
 * The page's content beside the peek: a flex row the open panel pushes at ≥ 1000 px. Pass the `<Peek>` element as
 * `peek`, so it is a flex item of the same row (below 1000 px it leaves the row and floats over the content).
 */
export function PeekLayout({ open, peek, className, children }: { open: boolean; peek?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <div className={cx('peek-layout', open && 'is-open', className)}>
      <div className="peek-layout-main">{children}</div>
      {peek}
    </div>
  );
}

/** The thing behind the top peek, as the breadcrumb says it: "sales order SO-757613". */
function describe(r: PeekRef): ReactNode {
  switch (r.kind) {
    case 'record': return <>{noun(r.cls)} <span className="font-mono">{r.key}</span></>;
    case 'document': return 'document';
    case 'run': return 'run';
    case 'system': return systemName(r.code);
    case 'integration': return stripSystem(r.process);
  }
}

const label = (r: PeekRef) => (r.kind === 'record' ? `${noun(r.cls)} ${r.key}` : r.kind === 'system' ? `System ${systemName(r.code)}` : r.kind === 'integration' ? `Integration ${r.process}` : r.kind);

/** The tag of a system or an integration, in the place of the noun tag of the others. */
function ThingTag({ kind }: { kind: 'system' | 'integration' }) {
  return (
    <span className="inline-flex items-center gap-1.5 h-6 rounded-full pl-1.5 pr-2.5 text-small font-medium whitespace-nowrap bg-n3 text-n9">
      <NetworkIcon size={14} />{kind === 'system' ? 'system' : 'integration'}
    </span>
  );
}

/**
 * Renders the open peek (nothing when none is open). `time` is the stage's time params (from, to), carried to a
 * record's card; `onStory` is what "Tell the story" does with the record it is pressed on. Documents clicked inside
 * open as a second peek, as do runs; the stack never grows past two.
 */
export function Peek({ time, onStory }: { time: URLSearchParams; onStory?: (ref: PeekRef) => void }) {
  const { stack, top, open, back, close } = usePeek();
  const navigate = useNavigate();
  const { link } = useEnv();
  const panel = useRef<HTMLElement>(null);
  const [copied, setCopied] = useState(false);
  const key = top ? encodePeek(top) : '';
  const deep = stack.length > 1;

  // Esc closes the top peek, unless something else is typing (the search field closes its own suggestions with it)
  useEffect(() => {
    if (!top) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target instanceof Element ? e.target : null;
      const typing = t?.closest('input, textarea, select, [contenteditable="true"], [role="menu"], [role="listbox"], [role="dialog"]');
      if (typing && !panel.current?.contains(typing)) return;
      e.preventDefault();
      if (deep) back();
      else close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [top, deep, back, close]);

  // The panel takes focus when it opens or changes, so Esc works at once; the copied state belongs to one peek
  useEffect(() => {
    if (key) panel.current?.focus({ preventScroll: true });
    setCopied(false);
  }, [key]);

  if (!top) return null;

  const openFull = () => {
    if (top.kind === 'document') {
      void navigate(link(documentPath(top.ref)), { viewTransition: true });
      return;
    }
    if (top.kind === 'system' || top.kind === 'integration') {
      void navigate(link(top.kind === 'system' ? systemPath(top.code) : integrationPath(top.process)));
      return;
    }
    // A record or a run has no page of its own: the stage with the peek open is the link
    navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => undefined);
  };

  return (
    <>
      <div className="peek-scrim mv-rise" onClick={close} aria-hidden="true" />
      <aside ref={panel} tabIndex={-1} aria-label={label(top)} className="peek-panel bg-n2 text-n10 shadow-float mv-rise outline-none">
        <div className="flex items-center gap-2 pl-4 pr-3 pt-3 pb-2 shrink-0 min-w-0">
          {deep && (
            <button type="button" onClick={back}
              className="inline-flex items-center gap-1 h-7 pl-1 pr-2.5 rounded-full bg-transparent border-0 cursor-pointer text-small text-n8 min-w-0 transition-colors duration-[var(--t-micro)] hover:bg-n3 hover:text-n10">
              <BackIcon size={14} /><span className="truncate">{describe(stack[0])}</span>
            </button>
          )}
          {top.kind === 'system' || top.kind === 'integration' ? <ThingTag kind={top.kind} /> : <NounTag kind={top.kind} />}
          {top.kind === 'record' && !deep && <Kicker className="truncate">{noun(top.cls)}</Kicker>}
          <span className="flex-1" />
          <button type="button" onClick={openFull} aria-live="polite"
            className="h-7 px-2.5 rounded-full bg-transparent border-0 cursor-pointer text-small text-n8 whitespace-nowrap transition-colors duration-[var(--t-micro)] hover:bg-n3 hover:text-n10">
            {top.kind === 'document' ? 'Open full' : top.kind === 'system' ? 'Open page' : top.kind === 'integration' ? 'Open page' : copied ? 'Link copied' : 'Copy link'}
          </button>
          <IconButton label="Close" onClick={close}><CloseIcon size={16} /></IconButton>
        </div>
        <div key={key} className="flex-1 min-h-0 overflow-y-auto px-6 pb-8">
          {top.kind === 'record' && (
            <RecordPeek cls={top.cls} recordKey={top.key} time={time}
              onMessage={(ref) => open({ kind: 'document', ref })}
              onRun={(executionId) => open({ kind: 'run', executionId })}
              onStory={() => onStory?.(top)} />
          )}
          {top.kind === 'document' && <DocumentPeek ref={top.ref} onRun={(executionId) => open({ kind: 'run', executionId })} />}
          {top.kind === 'run' && <RunPeek executionId={top.executionId} onMessage={(ref) => open({ kind: 'document', ref })} />}
          {top.kind === 'system' && <SystemPeek code={top.code} onIntegration={(process) => open({ kind: 'integration', process })} />}
          {top.kind === 'integration' && <IntegrationPeek process={top.process} onRun={(executionId) => open({ kind: 'run', executionId })} />}
        </div>
      </aside>
    </>
  );
}
